import { addDays, parseISO } from "date-fns";
import type React from "react";
import { useEffect, useState } from "react";

import type { UserDetailsDto, UserListItemDto } from "@/api/types";
import { DatePicker } from "@/components/DatePicker/DatePicker";
import { Modal } from "@/components/Modal/Modal";
import { Box } from "@/design-system/Box";
import { Button } from "@/design-system/Button";
import { Stack } from "@/design-system/Stack";
import { useCurrentDate } from "@/hooks/useCurrentDate";
import { userMutations } from "@/queries/users/mutations";
import { useTranslation } from "@/translations";

interface ScheduleUserDeletionModalProps {
  user: UserDetailsDto | UserListItemDto | null;
  projectIds: string[];
  isOpened: boolean;
  onOpenChange: (state: boolean) => void;
  onSetDeletionDate: () => void;
}

export function ScheduleUserDeletionModal({
  user,
  projectIds,
  isOpened,
  onOpenChange,
  onSetDeletionDate,
}: ScheduleUserDeletionModalProps): React.ReactNode {
  const clockDate = useCurrentDate();
  const [currDeletionDate, setCurrDeletionDate] = useState<Date>();

  const { t } = useTranslation();
  const setDeletionDateMutation = userMutations.useSetUserDeletionDate();

  useEffect(() => {
    if (user?.deleteAfter) {
      // oxlint-disable-next-line react-hooks/set-state-in-effect
      setCurrDeletionDate(parseISO(user.deleteAfter));
    }
  }, [user?.deleteAfter]);

  const handleResetDeletionDate = async () => {
    if (!user) return;

    await setDeletionDateMutation.mutateAsync({
      userId: user.id,
      projectIds,
      date: undefined,
    });

    setCurrDeletionDate(undefined);
    onOpenChange(false);
    onSetDeletionDate();
  };

  const handleSetDeletionDate = async () => {
    if (!user) return;

    await setDeletionDateMutation.mutateAsync({
      userId: user.id,
      projectIds,
      date: currDeletionDate ? new Date(currDeletionDate) : undefined,
    });

    onOpenChange(false);
    onSetDeletionDate();
  };

  return (
    <Modal.Root
      isOpened={isOpened}
      onOpenChange={onOpenChange}
      title={t("page.user-detail.delete-date-modal.title")}
      description={t("page.user-detail.delete-date-modal.description")}
      shouldCloseOnClickOutside={false}
      isPending={!user}
      isNested
    >
      <Modal.Body>
        <Box padding={{ y: "sm" }}>
          <Stack gap="xl">
            <DatePicker
              data-testid="deletion-date-picker"
              value={currDeletionDate}
              onChange={setCurrDeletionDate}
              min={addDays(clockDate, 1)}
            />
            {user && "bulkDeleted" in user && user.bulkDeleted && (
              <p className="text-caption text-text-secondary">
                {t("page.user-detail.delete-date-modal.disclaimer.project-specific-adjustment")}
              </p>
            )}
          </Stack>
        </Box>
      </Modal.Body>
      <Modal.Actions>
        {user?.deleteAfter && (
          <span className="mr-auto w-fit">
            <Button onClick={handleResetDeletionDate} variant="outline" isLoading={setDeletionDateMutation.isPending}>
              {t("page.user-detail.delete-date-modal.action.reset")}
            </Button>
          </span>
        )}
        <Button
          onClick={() => onOpenChange(false)}
          variant="outline"
          tone="primary"
          disabled={setDeletionDateMutation.isPending}
        >
          {t("common.action.cancel")}
        </Button>

        <Button
          data-testid="set-deletion-modal-confirm"
          onClick={handleSetDeletionDate}
          variant="filled"
          tone="warning"
          isLoading={setDeletionDateMutation.isPending}
        >
          {t("common.action.confirm")}
        </Button>
      </Modal.Actions>
    </Modal.Root>
  );
}
